import { useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { X, Plus, Trash2, Loader2, Save, GripVertical } from "lucide-react";
import { encuestasApi, reservasAdminApi, EncuestaPreguntaDto } from "../../lib/api";

type PreguntaForm = EncuestaPreguntaDto & { _key: string };

// Fijas: el tipo "rating" siempre tiene estas 5 opciones, en este orden, no editables.
const OPCIONES_RATING = ["Muy malo", "Malo", "Regular", "Bueno", "Muy bueno"].map((texto) => ({ texto }));

function nuevaPregunta(): PreguntaForm {
  return { _key: crypto.randomUUID(), tipo: "texto", textoPregunta: "", requerida: true };
}

export function CrearEncuestaModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [modo, setModo] = useState<"whatsapp" | "qr">("whatsapp");
  const [sucursalId, setSucursalId] = useState<number | "">("");
  const [preguntas, setPreguntas] = useState<PreguntaForm[]>([nuevaPregunta()]);
  const [error, setError] = useState<string | null>(null);

  const sucursalesDisp = useQuery({
    queryKey: ["encuestas", "sucursales-disponibles"],
    queryFn: () => reservasAdminApi.sucursales(),
  });

  const crearMut = useMutation({
    mutationFn: () =>
      encuestasApi.crear({
        titulo: titulo.trim(),
        descripcion: descripcion.trim() || undefined,
        modo,
        sucursalId: sucursalId || undefined,
        preguntas: preguntas.map(({ _key, ...p }) => p),
      }),
    onSuccess: onCreated,
    onError: (e: any) => setError(e.message || "Error creando la encuesta"),
  });

  function actualizarPregunta(key: string, patch: Partial<PreguntaForm>) {
    setPreguntas((ps) => ps.map((p) => (p._key === key ? { ...p, ...patch } : p)));
  }
  function agregarPregunta() {
    setPreguntas((ps) => [...ps, nuevaPregunta()]);
  }
  function quitarPregunta(key: string) {
    setPreguntas((ps) => ps.filter((p) => p._key !== key));
  }
  function cambiarTipo(key: string, tipo: "texto" | "seleccion" | "rating") {
    actualizarPregunta(key, {
      tipo,
      opciones: tipo === "seleccion" ? [{ texto: "" }, { texto: "" }] : tipo === "rating" ? OPCIONES_RATING : undefined,
    });
  }
  function actualizarOpcion(key: string, idx: number, texto: string) {
    setPreguntas((ps) =>
      ps.map((p) => {
        if (p._key !== key || !p.opciones) return p;
        const opciones = [...p.opciones];
        opciones[idx] = { texto };
        return { ...p, opciones };
      }),
    );
  }
  function agregarOpcion(key: string) {
    setPreguntas((ps) =>
      ps.map((p) => {
        if (p._key !== key || !p.opciones || p.opciones.length >= 5) return p;
        return { ...p, opciones: [...p.opciones, { texto: "" }] };
      }),
    );
  }
  function quitarOpcion(key: string, idx: number) {
    setPreguntas((ps) =>
      ps.map((p) => {
        if (p._key !== key || !p.opciones || p.opciones.length <= 2) return p;
        return { ...p, opciones: p.opciones.filter((_, i) => i !== idx) };
      }),
    );
  }

  function guardar() {
    setError(null);
    if (!titulo.trim()) return setError("Ingresá un título para la encuesta");
    if (preguntas.length === 0) return setError("Agregá al menos una pregunta");
    for (const p of preguntas) {
      if (!p.textoPregunta.trim()) return setError("Todas las preguntas necesitan un texto");
      if (p.tipo === "seleccion") {
        const validas = (p.opciones ?? []).filter((o) => o.texto.trim());
        if (validas.length < 2) return setError(`La pregunta "${p.textoPregunta}" necesita al menos 2 opciones con texto`);
      }
    }
    crearMut.mutate();
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}>

        <div className="flex items-center justify-between px-5 py-3 border-b border-ink-200">
          <h2 className="font-display font-bold text-ink-800">Nueva encuesta</h2>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><X className="size-5" /></button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {error && <div className="px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}

          <div>
            <label className="block text-xs font-medium text-ink-600 mb-1">Título *</label>
            <input className="input w-full" value={titulo} onChange={(e) => setTitulo(e.target.value)}
              placeholder="Ej: ¿Cómo fue tu experiencia hoy?" />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-600 mb-1">Descripción</label>
            <textarea className="input w-full" rows={2} value={descripcion} onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Texto introductorio que ve el cliente (opcional)" />
          </div>

          <div>
            <label className="block text-xs font-medium text-ink-600 mb-1.5">Cómo se distribuye</label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setModo("whatsapp")}
                className={`text-left px-3 py-2.5 rounded-lg border text-sm transition-colors ${
                  modo === "whatsapp" ? "border-brand-500 bg-brand-50/50 text-brand-800" : "border-ink-200 text-ink-600 hover:border-ink-300"
                }`}>
                <span className="font-medium block">WhatsApp masivo</span>
                <span className="text-xs text-ink-500">Un link por cliente conocido, ya identificado</span>
              </button>
              <button type="button" onClick={() => setModo("qr")}
                className={`text-left px-3 py-2.5 rounded-lg border text-sm transition-colors ${
                  modo === "qr" ? "border-brand-500 bg-brand-50/50 text-brand-800" : "border-ink-200 text-ink-600 hover:border-ink-300"
                }`}>
                <span className="font-medium block">Enlace QR genérico</span>
                <span className="text-xs text-ink-500">Un solo link para todos (ej. QR en la mesa); pide nombre y cédula/teléfono al inicio</span>
              </button>
            </div>
          </div>

          {(sucursalesDisp.data?.length ?? 0) > 1 && (
            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1">Sucursal</label>
              <select className="input w-full" value={sucursalId}
                onChange={(e) => setSucursalId(e.target.value ? Number(e.target.value) : "")}>
                <option value="">Sin asignar (usa la primera sucursal activa)</option>
                {sucursalesDisp.data!.map((s) => (
                  <option key={s.id} value={s.id}>{s.nombre}</option>
                ))}
              </select>
              <p className="text-xs text-ink-400 mt-1">Define el logo/color de la encuesta y, en modo WhatsApp, a qué clientes se envía.</p>
            </div>
          )}

          <hr className="border-ink-200" />
          <h3 className="text-sm font-bold text-ink-800">Preguntas</h3>

          <div className="space-y-3">
            {preguntas.map((p, idx) => (
              <div key={p._key} className="border border-ink-200 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <GripVertical className="size-4 text-ink-300 shrink-0" />
                  <span className="text-xs font-semibold text-ink-500 shrink-0">#{idx + 1}</span>
                  <input className="input flex-1 text-sm" placeholder="Texto de la pregunta"
                    value={p.textoPregunta} onChange={(e) => actualizarPregunta(p._key, { textoPregunta: e.target.value })} />
                  <select className="input text-sm w-36 shrink-0" value={p.tipo}
                    onChange={(e) => cambiarTipo(p._key, e.target.value as "texto" | "seleccion" | "rating")}>
                    <option value="texto">Texto libre</option>
                    <option value="seleccion">Selección múltiple</option>
                    <option value="rating">Calificación (1-5 fija)</option>
                  </select>
                  {preguntas.length > 1 && (
                    <button onClick={() => quitarPregunta(p._key)} className="text-red-500 hover:bg-red-50 rounded p-1 shrink-0">
                      <Trash2 className="size-4" />
                    </button>
                  )}
                </div>

                {p.tipo === "seleccion" && (
                  <div className="pl-6 space-y-1.5">
                    {(p.opciones ?? []).map((o, oi) => (
                      <div key={oi} className="flex items-center gap-2">
                        <span className="text-xs text-ink-400 w-4">{oi + 1}.</span>
                        <input className="input flex-1 text-sm py-1.5" placeholder={`Opción ${oi + 1}`}
                          value={o.texto} onChange={(e) => actualizarOpcion(p._key, oi, e.target.value)} />
                        {(p.opciones?.length ?? 0) > 2 && (
                          <button onClick={() => quitarOpcion(p._key, oi)} className="text-ink-400 hover:text-red-500 shrink-0">
                            <X className="size-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                    {(p.opciones?.length ?? 0) < 5 && (
                      <button onClick={() => agregarOpcion(p._key)}
                        className="text-xs text-brand-600 hover:underline inline-flex items-center gap-1 mt-1">
                        <Plus className="size-3" /> Agregar opción
                      </button>
                    )}
                  </div>
                )}

                {p.tipo === "rating" && (
                  <div className="pl-6 flex flex-wrap gap-1.5">
                    {OPCIONES_RATING.map((o, oi) => (
                      <span key={oi} className="text-xs px-2 py-1 rounded-md bg-ink-100 text-ink-500">{o.texto}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <button onClick={agregarPregunta}
            className="w-full py-2 rounded-lg border border-dashed border-ink-300 text-ink-500 hover:border-brand-400 hover:text-brand-600 text-sm inline-flex items-center justify-center gap-1.5">
            <Plus className="size-4" /> Agregar pregunta
          </button>
        </div>

        <div className="px-5 py-3 border-t border-ink-200 flex items-center justify-end gap-2 bg-ink-50">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button onClick={guardar} disabled={crearMut.isPending}
            className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
            {crearMut.isPending ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
            Crear encuesta
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
